<template>
  <div
    role="main"
    aria-label="Détails de la délégation logistique"
    tabindex="-1"
  >
    <b-card
      v-if="delegation"
      no-body
      aria-labelledby="delegation-title"
      aria-describedby="delegation-desc"
      role="region"
      class="mb-2"
    >
      <b-card-header>
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <h3
              id="delegation-title"
              class="card-title"
            >
              {{ delegation.name }}
            </h3>
            <span
              id="delegation-desc"
              class="sr-only"
            >Informations détaillées sur la délégation logistique, y compris localités, commandes et statut d’activation.</span>
          </div>
          <div class="ml-5">
            <b-button
              v-if="user.role === 'admin'"
              :variant="delegation.is_active ? 'outline-danger' : 'outline-success'"
              :aria-pressed="delegation.is_active ? 'true' : 'false'"
              :aria-label="delegation.is_active ? 'Désactiver cette délégation pour la logistique' : 'Activer cette délégation pour la logistique'"
              @click="toggleStatus"
            >
              {{ delegation.is_active ? 'Désactiver' : 'Activer' }}
            </b-button>
            <b-badge
              :variant="delegation.is_active ? 'success' : 'danger'"
              class="ml-2"
              :aria-label="delegation.is_active ? 'Statut logistique : activé' : 'Statut logistique : désactivé'"
            >
              {{ delegation.is_active ? 'Activé' : 'Désactivé' }}
            </b-badge>
          </div>
        </div>
      </b-card-header>

      <b-card-body>
        <b-row>
          <b-col
            lg="5"
            md="6"
            sm="12"
          >
            <b-card
              title="Informations territoriales"
              aria-label="Informations territoriales de la délégation"
            >
              <b-list-group flush>
                <b-list-group-item>
                  <strong>Nom officiel&nbsp;:</strong> <span aria-label="Nom de la délégation">{{ delegation.name }}</span>
                </b-list-group-item>
                <b-list-group-item>
                  <strong>Gouvernorat&nbsp;:</strong>
                  <router-link
                    :to="{ name: 'governorate-detail', params: { id: delegation.governorate.id }}"
                    :aria-label="`Voir le gouvernorat de rattachement ${delegation.governorate.name}`"
                  >
                    {{ delegation.governorate.name }}
                  </router-link>
                </b-list-group-item>
                <b-list-group-item>
                  <strong>Nombre de localités&nbsp;:</strong> <span aria-label="Nombre de localités desservies">{{ delegation.active_localities_count }}</span>
                </b-list-group-item>
                <b-list-group-item>
                  <strong>Nombre de commandes&nbsp;:</strong> <span aria-label="Nombre total de commandes logistiques">{{ delegation.num_orders }}</span>
                </b-list-group-item>
                <b-list-group-item>
                  <strong>État logistique&nbsp;:</strong> <span aria-label="Statut logistique">{{ delegation.active_status }}</span>
                </b-list-group-item>
              </b-list-group>
              <span class="sr-only">Utilisez les boutons d’action pour modifier ou changer le statut logistique de la délégation.</span>
            </b-card>
          </b-col>
          <b-col
            lg="7"
            md="6"
            sm="12"
          >
            <b-card
              title="Localités desservies"
              aria-label="Liste des localités desservies"
            >
              <div class="mb-2">
                <b-form-input
                  v-model="localitySearch"
                  placeholder="Rechercher une localité desservie..."
                  class="mb-2"
                  aria-label="Recherche de localité desservie"
                />
                <span class="sr-only">Filtrez la liste des localités desservies par nom ou code postal.</span>
              </div>
              <div v-if="filteredLocalities.length > 0">
                <b-table
                  :items="filteredLocalities"
                  :fields="localityFields"
                  responsive="sm"
                  show-empty
                  aria-label="Tableau des localités desservies"
                  role="table"
                >
                  <template #cell(actions)="data">
                    <router-link
                      :to="{ name: 'localities-detail', params: { id: data.item.id }}"
                      class="btn btn-sm btn-outline-primary"
                      :aria-label="`Voir les détails de la localité ${data.item.name}`"
                    >
                      <feather-icon
                        icon="EyeIcon"
                        size="14"
                      />
                    </router-link>
                  </template>
                </b-table>
              </div>
              <div
                v-else
                class="text-center"
                aria-live="polite"
              >
                <p>Aucune localité desservie trouvée</p>
              </div>
            </b-card>
          </b-col>
        </b-row>

        <b-row class="mt-3">
          <b-col cols="12">
            <b-card
              title="Statistiques logistiques"
              aria-label="Statistiques logistiques de la délégation"
            >
              <apexchart
                v-if="pieChartOptions && pieChartSeries"
                type="pie"
                height="350"
                :options="pieChartOptions"
                :series="pieChartSeries"
                aria-label="Graphique des statistiques logistiques"
              />
              <span class="sr-only">Ce graphique présente le nombre de localités desservies et de commandes pour cette délégation.</span>
            </b-card>
          </b-col>
        </b-row>

        <!-- Replace existing table with the new component -->
        <orders-table
          v-if="delegation.recent_orders && delegation.recent_orders.length > 0"
          :orders="delegation.recent_orders"
          :total-orders="delegation.num_orders"
          :entity-id="delegation.id"
          entity-type="delegation"
          card-title="Dernières commandes logistiques"
          class="mt-3"
          aria-label="Tableau des dernières commandes logistiques pour cette délégation"
        />

        <b-row class="mt-2">
          <b-col cols="12">
            <b-button
              v-if="isAdmin"
              variant="primary"
              class="mr-1"
              :to="{ name: 'delegation-edit', params: { id: $route.params.id }}"
              aria-label="Modifier les informations territoriales de la délégation"
            >
              <feather-icon
                icon="EditIcon"
                class="mr-50"
                size="16"
              />
              <span>Modifier</span>
            </b-button>
            <b-button
              variant="outline-primary"
              :to="{ name: 'delegation-list' }"
              aria-label="Retour à la liste des délégations"
            >
              <feather-icon
                icon="ListIcon"
                class="mr-50"
                size="16"
              />
              <span>Liste des délégations</span>
            </b-button>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>
    <div
      v-else
      class="text-center p-5"
      aria-live="polite"
      role="status"
    >
      <b-spinner variant="primary" />
      <p class="mt-2">
        Chargement des détails de la délégation pour la logistique...
      </p>
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BRow,
  BCol,
  BListGroup,
  BListGroupItem,
  BBadge,
  BButton,
  BSpinner,
  BTable,
  BFormInput,
} from 'bootstrap-vue'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import VueApexCharts from 'vue-apexcharts'
import storeAuth from '@/store/store'
import OrdersTable from '@/components/tables/OrdersTable.vue'

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BListGroup,
    BListGroupItem,
    BBadge,
    BButton,
    BSpinner,
    BTable,
    BFormInput,
    apexchart: VueApexCharts,
    OrdersTable,
  },
  data() {
    return {
      delegation: null,
      isLoading: false,
      user: {},
      localitySearch: '',
      localityFields: [
        { key: 'name', label: 'Nom' },
        { key: 'zip_code', label: 'Code Postal' },
        { key: 'actions', label: 'Actions' },
      ],
      orderFields: [
        { key: 'order_number', label: 'Numéro de commande' },
        { key: 'customer_name', label: 'Client' },
        { key: 'total_amount', label: 'Montant total' },
        { key: 'status', label: 'Statut' },
        { key: 'actions', label: 'Actions' },
      ],
      pieChartOptions: {
        labels: ['Localités', 'Commandes'],
        colors: ['#28C76F', '#7367F0'],
        legend: {
          position: 'bottom',
        },
        responsive: [
          {
            breakpoint: 480,
            options: {
              chart: {
                width: 200,
              },
              legend: {
                position: 'bottom',
              },
            },
          },
        ],
      },
      pieChartSeries: [],
    }
  },
  computed: {
    isAdmin() {
      return this.user.role === 'admin'
    },
    filteredLocalities() {
      if (!this.delegation || !this.delegation.localities) return []

      return this.localitySearch
        ? this.delegation.localities.filter(l => l.name.toLowerCase().includes(this.localitySearch.toLowerCase())
            || l.zip_code.includes(this.localitySearch))
        : this.delegation.localities
    },
  },
  created() {
    this.fetchDelegationDetails()
    this.user = storeAuth.state.user
  },
  methods: {
    async fetchDelegationDetails() {
      this.isLoading = true
      try {
        const { data } = await axios.get(`/api/delegations/detail/${this.$route.params.id}/`)
        this.delegation = data

        // Setup chart data
        this.pieChartSeries = [
          this.delegation.active_localities_count || 0,
          this.delegation.num_orders || 0,
        ]

        this.isLoading = false
      } catch (error) {
        this.showToast('danger', 'top-center', 'Erreur lors du chargement des détails de la délégation')
        this.isLoading = false
      }
    },
    async toggleStatus() {
      try {
        // Show confirmation dialog before proceeding
        const result = await this.$swal({
          title: this.delegation.is_active ? 'Désactiver la délégation?' : 'Activer la délégation?',
          text: this.delegation.is_active
            ? 'Cette action empêchera les livraisons dans cette délégation. Êtes-vous sûr?'
            : 'Cette action permettra les livraisons dans cette délégation. Êtes-vous sûr?',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Oui, confirmer',
          cancelButtonText: 'Annuler',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        })

        // If user confirmed the action
        if (result.value) {
          const { data } = await axios.post(`/api/delegations/toggle-status/${this.$route.params.id}/`)
          this.delegation.is_active = data.is_active
          this.showToast('success', 'top-center',
            `La délégation a été ${data.is_active ? 'activée' : 'désactivée'} avec succès`)
        }
      } catch (error) {
        console.error('Error toggling delegation status:', error)
        this.showToast('danger', 'top-center', 'Erreur lors du changement de statut')
      }
    },
    viewAllOrders() {
      // Navigate to orders list with delegation filter
      this.$router.push({
        name: 'order-list',
        query: { delegation: this.$route.params.id },
      })
    },
    showToast(variant, position, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Notification',
          icon: 'InfoIcon',
          text,
          variant,
        },
      }, {
        position,
      })
    },
  },
}
</script>
